import type { ComputedRef, Ref } from 'vue'
import type { ErrorCollectionWithTraces } from '@/helpers/errors'
import { useMergedCollection, useSimpleErrorCollection } from '@/helpers/errors'
import type { BackendError } from '@/helpers/errors/collection'
import { useFrontendError } from '@/composables/useFrontendError'
import { useEmailError } from '@/composables/useEmailError'
import type { Email } from '@/types/email'

export interface EmailAndNamesFieldsError {
    submit(): void
    errorsWithFeValidation: ComputedRef<ErrorCollectionWithTraces>
}

export function useEmailAndNamesFieldsError(
    isLogged: Readonly<Ref<boolean>>,
    apiErrors: Readonly<Ref<ErrorCollectionWithTraces>>,
    names: Readonly<Ref<string>>,
    email: Readonly<Ref<string>>,
    validatedEmail: Ref<Email | ''>,
    submitHandler: () => void
): EmailAndNamesFieldsError {
    const namesErrorValue: BackendError = {
        message: 'Please enter your name.',
        path: ['customer', 'firstName'],
        isInternalServerError: false,
        isUnauthorized: false,
    }

    const { error: namesError, validate: validateName } = useFrontendError(
        names,
        (value) => {
            if (!value) {
                return {
                    valid: false,
                    error: namesErrorValue,
                }
            }
            return {
                valid: true,
                value: value,
            }
        }
    )

    const { error: emailError, validate: validateEmail } = useEmailError(
        email,
        ['customer', 'email']
    )

    const collection = useSimpleErrorCollection([namesError, emailError])
    const errorsWithFeValidation = useMergedCollection(apiErrors, [collection])

    const submit = () => {
        const validEmail = validateEmail() || ''

        if (!isLogged.value && (!validateName() || !validEmail)) {
            return
        }

        validatedEmail.value = validEmail

        submitHandler()
    }

    return {
        submit,
        errorsWithFeValidation,
    }
}
